import { roundToCents } from "@common/helpers/monetary";
import { MatchableItem } from "./types";
import { parseTransactionMatchId } from "./mappers";

export interface DocumentMatchDiff {
    txIdsToAdd: number[];
    addAmount: number;
    txIdsToRemove: number[];
    removedAmount: number;
}

export function computeDocumentMatchDiff(
    selectedItems: MatchableItem[],
    originalItems: MatchableItem[],
): DocumentMatchDiff {
    const selectedById = new Map(selectedItems.map((i) => [i.id, i]));
    const originalById = new Map(originalItems.map((i) => [i.id, i]));

    const txIdsToAdd: number[] = [];
    let addAmount = 0;
    for (const item of selectedItems) {
        if (!originalById.has(item.id)) {
            txIdsToAdd.push(parseTransactionMatchId(item.id));
            addAmount += Math.abs(item.amount);
        }
    }

    const txIdsToRemove: number[] = [];
    let removedAmount = 0;
    for (const item of originalItems) {
        if (!selectedById.has(item.id)) {
            txIdsToRemove.push(parseTransactionMatchId(item.id));
            removedAmount += Math.abs(item.amount);
        }
    }

    return {
        txIdsToAdd,
        addAmount: roundToCents(addAmount),
        txIdsToRemove,
        removedAmount: roundToCents(removedAmount),
    };
}

export const SELECT_TRANSACTION_ALLOCATION_REASON =
    "Select a transaction first";
export const PENDING_CHANGES_ALLOCATION_REASON =
    "Confirm or undo your other match changes first";

export type AllocationOpenTarget =
    | { kind: "single" }
    | { kind: "all" }
    | { kind: "blocked"; reason: string };

export function getAllocationBlockedReason({
    diff,
    items,
}: {
    diff: DocumentMatchDiff;
    items: MatchableItem[];
}): string | null {
    const itemTransactionIds = new Set(
        items.map((item) => parseTransactionMatchId(item.id)),
    );
    const hasOtherPendingChanges =
        diff.txIdsToRemove.length > 0 ||
        diff.txIdsToAdd.some((id) => !itemTransactionIds.has(id));
    if (hasOtherPendingChanges) {
        return PENDING_CHANGES_ALLOCATION_REASON;
    }
    return null;
}

export function getAllocationOpenTarget({
    diff,
    selectedItems,
}: {
    diff: DocumentMatchDiff;
    selectedItems: MatchableItem[];
}): AllocationOpenTarget {
    if (selectedItems.length === 0) {
        return {
            kind: "blocked",
            reason: SELECT_TRANSACTION_ALLOCATION_REASON,
        };
    }

    const blockedReason = getAllocationBlockedReason({
        diff,
        items: selectedItems,
    });
    if (blockedReason) {
        return { kind: "blocked", reason: blockedReason };
    }

    return selectedItems.length === 1 ? { kind: "single" } : { kind: "all" };
}
